// src/components/roads/page/RoadActionModals.js
// Road action modals — extracted from RoadWorksPage v4.7.
// --- UPDATE: v5.0 — AI sync modal now previews every proposed change (current vs
// AI-derived condition and status), skips roads without an RUL prediction, can be
// stopped mid-run, and reports per-road results. Inspection date can't be in the past.
import React, { useMemo, useRef, useState } from 'react';
import {
    Badge, Box, Button, Flex, FormControl, FormLabel, HStack, Icon, Input, Modal,
    ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    NumberDecrementStepper, NumberIncrementStepper, NumberInput, NumberInputField,
    NumberInputStepper, Progress, Select, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr,
    VStack, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import {
    MdAssessment, MdSchedule, MdReportProblem, MdArrowForward, MdCheckCircle, MdError,
} from 'react-icons/md';
import * as roadService from '../../../services/roadService';
import { CONDITION_CFG } from './shared';
import { AiChip, AiSparkleIcon, ModelTag } from './AiMlIcons';

// ─────────────────────────────────────────────────────────────────────────────
// CONDITION ASSESSMENT MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const ConditionAssessmentModal = ({ isOpen, onClose, road, onSubmit }) => {
    const [score, setScore] = useState(75);
    const [notes, setNotes] = useState('');
    const [distress, setDistress] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const distressOptions = ['cracking','potholes','rutting','raveling','bleeding','patching','edge_break'];

    const derivedRating = useMemo(() =>
        score >= 85 ? 'excellent'
        : score >= 70 ? 'good'
        : score >= 55 ? 'fair'
        : score >= 40 ? 'poor'
        : 'critical', [score]);

    const cfg = CONDITION_CFG[derivedRating];

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await onSubmit({
                condition_score: score,
                condition_rating: derivedRating,
                distress_types: distress,
                inspector_notes: notes,
            });
            onClose();
            setScore(75); setNotes(''); setDistress([]);
        } finally {
            setSubmitting(false);
        }
    };

    const toggleDistress = (d) => {
        setDistress(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="2xl">
                <ModalHeader>
                    <HStack>
                        <Box bg="blue.500" p={2} borderRadius="lg">
                            <Icon as={MdAssessment} color="white" boxSize={5} />
                        </Box>
                        <Box>
                            <Text fontSize="md" fontWeight="800">Condition Assessment</Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="500">
                                {road?.name || 'Road segment'}
                            </Text>
                        </Box>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <FormControl>
                            <FormLabel fontSize="sm">Condition Score (0–100)</FormLabel>
                            <HStack>
                                <NumberInput value={score} min={0} max={100} flex={1}
                                    onChange={(_, n) => setScore(isNaN(n) ? 0 : n)}>
                                    <NumberInputField />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                                <Badge colorScheme={cfg.color} fontSize="sm" px={3} py={1}
                                    borderRadius="full">
                                    {cfg.label}
                                </Badge>
                            </HStack>
                            <Progress value={score} colorScheme={cfg.color} size="sm"
                                borderRadius="full" mt={2} hasStripe isAnimated />
                            {/* --- UPDATE: auto-flag hint */}
                            {score < 55 && (
                                <Text fontSize="xs" color="orange.500" mt={1.5}>
                                    Scores below 55 auto-flag this road as Maintenance Required.
                                </Text>
                            )}
                            {/* --- END UPDATE */}
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="sm">Distress Types Observed</FormLabel>
                            <Flex wrap="wrap" gap={2}>
                                {distressOptions.map(d => (
                                    <Badge key={d}
                                        as="button" type="button"
                                        colorScheme={distress.includes(d) ? 'orange' : 'gray'}
                                        cursor="pointer" px={3} py={1} borderRadius="full"
                                        onClick={() => toggleDistress(d)}
                                        aria-pressed={distress.includes(d)}
                                        _hover={{ transform:'scale(1.05)' }}
                                        transition="all 0.15s" fontSize="xs">
                                        {distress.includes(d) && <Icon as={CheckIcon} mr={1} boxSize={2.5} />}
                                        {d.replace(/_/g, ' ')}
                                    </Badge>
                                ))}
                            </Flex>
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="sm">Inspector Notes</FormLabel>
                            <Textarea value={notes} onChange={e => setNotes(e.target.value)}
                                placeholder="Optional observations…" rows={3} fontSize="sm" />
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="blue" onClick={handleSubmit} isLoading={submitting}
                        leftIcon={<Icon as={MdAssessment} />}>
                        Record Assessment
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// SCHEDULE INSPECTION MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const ScheduleInspectionModal = ({ isOpen, onClose, road, onSubmit }) => {
    const [date, setDate] = useState('');
    const [type, setType] = useState('routine');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    // --- UPDATE: no past dates
    const minDate = useMemo(() => {
        const d = new Date();
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().slice(0, 16);
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
    const isPast = date && date < minDate;
    // --- END UPDATE

    const handleSubmit = async () => {
        if (!date || isPast) return;
        setSubmitting(true);
        try {
            await onSubmit({ scheduled_date: date, inspection_type: type, notes });
            onClose();
            setDate(''); setType('routine'); setNotes('');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="2xl">
                <ModalHeader>
                    <HStack>
                        <Box bg="cyan.500" p={2} borderRadius="lg">
                            <Icon as={MdSchedule} color="white" boxSize={5} />
                        </Box>
                        <Box>
                            <Text fontSize="md" fontWeight="800">Schedule Inspection</Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="500">
                                {road?.name || 'Road segment'}
                            </Text>
                        </Box>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <FormControl isRequired isInvalid={!!isPast}>
                            <FormLabel fontSize="sm">Scheduled Date</FormLabel>
                            <Input type="datetime-local" value={date} min={minDate}
                                onChange={e => setDate(e.target.value)} />
                            {isPast && <Text fontSize="xs" color="red.500" mt={1}>Choose a future date and time.</Text>}
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="sm">Inspection Type</FormLabel>
                            <Select value={type} onChange={e => setType(e.target.value)}>
                                <option value="routine">Routine</option>
                                <option value="condition_survey">Condition Survey</option>
                                <option value="post_repair">Post-Repair</option>
                                <option value="emergency">Emergency</option>
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="sm">Notes</FormLabel>
                            <Textarea value={notes} onChange={e => setNotes(e.target.value)}
                                placeholder="Optional notes…" rows={3} fontSize="sm" />
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="cyan" onClick={handleSubmit} isLoading={submitting}
                        isDisabled={!date || !!isPast} leftIcon={<Icon as={MdSchedule} />}>
                        Schedule
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// REPORT ISSUE MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const ReportIssueModal = ({ isOpen, onClose, road, onSubmit }) => {
    const [issueType, setIssueType] = useState('pothole');
    const [description, setDescription] = useState('');
    const [severity, setSeverity] = useState('medium');
    const [reporterName, setReporterName] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!description) return;
        setSubmitting(true);
        try {
            await onSubmit({
                issue_type: issueType,
                description,
                severity,
                reporter_name: reporterName,
            });
            onClose();
            setDescription(''); setReporterName(''); setSeverity('medium');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="2xl">
                <ModalHeader>
                    <HStack>
                        <Box bg="orange.500" p={2} borderRadius="lg">
                            <Icon as={MdReportProblem} color="white" boxSize={5} />
                        </Box>
                        <Box>
                            <Text fontSize="md" fontWeight="800">Report Issue</Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="500">
                                {road?.name || 'Road segment'}
                            </Text>
                        </Box>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <FormControl>
                            <FormLabel fontSize="sm">Issue Type</FormLabel>
                            <Select value={issueType} onChange={e => setIssueType(e.target.value)}>
                                <option value="pothole">Pothole</option>
                                <option value="cracking">Surface Cracking</option>
                                <option value="flooding">Drainage / Flooding</option>
                                <option value="signage">Signage</option>
                                <option value="lighting">Lighting</option>
                                <option value="line_marking">Line Marking</option>
                                <option value="debris">Debris</option>
                                <option value="other">Other</option>
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="sm">Severity</FormLabel>
                            <Select value={severity} onChange={e => setSeverity(e.target.value)}>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High (auto-creates work order)</option>
                                <option value="critical">Critical (auto-creates work order)</option>
                            </Select>
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel fontSize="sm">Description</FormLabel>
                            <Textarea value={description} onChange={e => setDescription(e.target.value)}
                                placeholder="Describe the issue…" rows={3} fontSize="sm" />
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="sm">Reporter Name (optional)</FormLabel>
                            <Input value={reporterName} onChange={e => setReporterName(e.target.value)}
                                placeholder="Anonymous if blank" />
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="orange" onClick={handleSubmit} isLoading={submitting}
                        isDisabled={!description} leftIcon={<Icon as={MdReportProblem} />}>
                        Submit Report
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// 🚀 v4.6: AI ROAD STATE SYNC MODAL
// Translates predicted RUL into live condition assessments and broadcasts them
// to the WebSocket network.
// ─────────────────────────────────────────────────────────────────────────────

// Same RUL → score → rating mapping as v4.6 (unchanged thresholds)
export const deriveStateFromRul = (rulYears) => {
    let newScore = 85;
    if (rulYears <= 1) newScore = 25;
    else if (rulYears <= 2.5) newScore = 40;
    else if (rulYears <= 5) newScore = 55;
    else if (rulYears <= 8) newScore = 70;

    let newRating = 'excellent';
    if (newScore <= 30) newRating = 'critical';
    else if (newScore <= 50) newRating = 'poor';
    else if (newScore <= 65) newRating = 'fair';
    else if (newScore <= 80) newRating = 'good';

    return { newScore, newRating };
};

export const AIRoadStateSyncModal = ({ isOpen, onClose, selectedRoadIds, roads, onComplete, logger }) => {
    const [isSyncing, setIsSyncing] = useState(false);
    const [progress, setProgress] = useState(0);
    // --- UPDATE
    const [results, setResults] = useState({});   // roadId → 'ok' | 'error'
    const cancelRef = useRef(false);
    const headBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    // --- END UPDATE
    const toast = useToast();

    // --- UPDATE: preview of every proposed change before anything is written
    const plan = useMemo(() => roads
        .filter(r => selectedRoadIds.has(r.id))
        .map((road) => {
            const rulYears = road.ai_rul?.predicted_rul_years;
            if (rulYears == null) return { road, rulYears: null, skip: true };
            const { newScore, newRating } = deriveStateFromRul(rulYears);
            let newStatus = road.status;
            if (newScore <= 50 && road.status !== 'maintenance_required') newStatus = 'maintenance_required';
            else if (newScore > 50 && road.status === 'maintenance_required') newStatus = 'operational';
            return { road, rulYears, newScore, newRating, newStatus, skip: false };
        }), [roads, selectedRoadIds]);
    const actionable = plan.filter(p => !p.skip);
    // --- END UPDATE

    const handleSync = async () => {
        setIsSyncing(true);
        setProgress(0);
        setResults({});
        cancelRef.current = false;
        let successCount = 0;

        for (let i = 0; i < actionable.length; i++) {
            if (cancelRef.current) break;
            const { road, rulYears, newScore, newRating, newStatus } = actionable[i];
            try {
                // 1. Update Condition (Backend automatically broadcasts 'assessment_recorded' to WebSockets)
                await roadService.recordConditionAssessment(road.id, {
                    condition_score: newScore,
                    condition_rating: newRating,
                    distress_types: ['ai_inferred'],
                    inspector_notes: `System Auto-Sync: Condition set by AI Predicted RUL of ${rulYears} years.`,
                });

                // 2. Automatically update Operational Status based on the new condition
                if (newStatus !== road.status) {
                    await roadService.bulkUpdateRoadStatus([road.id], newStatus);
                }

                successCount++;
                setResults(prev => ({ ...prev, [road.id]: 'ok' }));
            } catch (err) {
                logger?.error(`Failed to sync road ${road.id}`, err);
                setResults(prev => ({ ...prev, [road.id]: 'error' }));
            }
            setProgress(Math.round(((i + 1) / actionable.length) * 100));
        }

        setIsSyncing(false);
        const stopped = cancelRef.current;
        toast({
            title: stopped ? 'AI Sync Stopped' : 'AI Sync Complete',
            description: `Synchronized condition and status for ${successCount} road(s). Network broadcast sent.`,
            status: stopped ? 'warning' : 'success',
            duration: 5000,
            isClosable: true
        });
        onClose();
        onComplete();
    };

    return (
        <Modal isOpen={isOpen} onClose={!isSyncing ? onClose : () => {}} isCentered size="xl" scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="2xl">
                <ModalHeader>
                    <HStack>
                        <Box bgGradient="linear(135deg, purple.500, pink.500)" p={2} borderRadius="lg">
                            <Icon as={AiSparkleIcon} color="white" boxSize={5} />
                        </Box>
                        <Box>
                            <HStack spacing={2}>
                                <Text fontSize="md" fontWeight="800">AI State Synchronization</Text>
                                <ModelTag model="rsf" />
                            </HStack>
                            <Text fontSize="xs" color="gray.500" fontWeight="500">
                                Translate RUL predictions into live condition states
                            </Text>
                        </Box>
                    </HStack>
                </ModalHeader>
                {!isSyncing && <ModalCloseButton />}
                <ModalBody py={4}>
                    <Text fontSize="sm" color="gray.500" mb={3}>
                        Review the changes below. Each road gets an AI-derived condition assessment and, where needed,
                        a status change — both are broadcast to the live WebSocket network.
                    </Text>
                    {isSyncing && (
                        <VStack spacing={2} align="stretch" mb={3}>
                            <Text fontSize="sm" fontWeight="600" textAlign="center" color="pink.500">
                                Syncing Intelligence... {progress}%
                            </Text>
                            <Progress value={progress} size="md" colorScheme="pink" borderRadius="full" hasStripe isAnimated />
                        </VStack>
                    )}
                    {/* --- UPDATE: change preview */}
                    <Box borderWidth="1px" borderRadius="xl" overflow="hidden">
                        <Table size="sm">
                            <Thead bg={headBg}>
                                <Tr>
                                    <Th>Road</Th>
                                    <Th isNumeric>RUL</Th>
                                    <Th>Condition</Th>
                                    <Th>Status</Th>
                                    <Th w="28px" />
                                </Tr>
                            </Thead>
                            <Tbody>
                                {plan.map(({ road, rulYears, newRating, newStatus, skip }) => {
                                    const cfg = CONDITION_CFG[newRating] || {};
                                    const res = results[road.id];
                                    return (
                                        <Tr key={road.id} opacity={skip ? 0.55 : 1}>
                                            <Td maxW="180px"><Text fontSize="xs" fontWeight="700" noOfLines={1}>{road.name || road.id}</Text></Td>
                                            <Td isNumeric fontSize="xs">{rulYears != null ? `${Number(rulYears).toFixed(1)}y` : '—'}</Td>
                                            <Td>
                                                {skip ? (
                                                    <Text fontSize="xs" color="gray.500">No prediction — skipped</Text>
                                                ) : (
                                                    <HStack spacing={1}>
                                                        <Badge fontSize="2xs" colorScheme={CONDITION_CFG[road.condition_rating]?.color || 'gray'}>
                                                            {road.condition_rating || 'unrated'}
                                                        </Badge>
                                                        <Icon as={MdArrowForward} boxSize={3} color="gray.400" />
                                                        <Badge fontSize="2xs" colorScheme={cfg.color}>{newRating}</Badge>
                                                    </HStack>
                                                )}
                                            </Td>
                                            <Td>
                                                {!skip && (newStatus !== road.status ? (
                                                    <Badge fontSize="2xs" colorScheme={newStatus === 'operational' ? 'green' : 'orange'}>
                                                        → {String(newStatus).replace(/_/g, ' ')}
                                                    </Badge>
                                                ) : (
                                                    <Text fontSize="xs" color="gray.500">unchanged</Text>
                                                ))}
                                            </Td>
                                            <Td>
                                                {res === 'ok' && <Icon as={MdCheckCircle} color="green.400" />}
                                                {res === 'error' && <Icon as={MdError} color="red.400" />}
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </Box>
                    <HStack mt={3} spacing={2}>
                        <AiChip kind="forecast" size="xs" />
                        <Text fontSize="xs" color="gray.500">
                            {actionable.length} of {plan.length} selected road(s) have an RUL prediction.
                        </Text>
                    </HStack>
                    {/* --- END UPDATE */}
                </ModalBody>
                <ModalFooter>
                    {!isSyncing ? (
                        <>
                            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                            <Button colorScheme="pink" onClick={handleSync} leftIcon={<Icon as={AiSparkleIcon} />}
                                isDisabled={actionable.length === 0}>
                                Sync {actionable.length} road{actionable.length === 1 ? '' : 's'}
                            </Button>
                        </>
                    ) : (
                        <Button variant="outline" colorScheme="red" onClick={() => { cancelRef.current = true; }}>
                            Stop after current road
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
